import { useState, useRef, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getTranscribedText } from "api/deepgram";
import { getUsageData } from "api/usage";
import AddIcon from "components/icons/add-icon";
import useCreateNotes from "zustand-store/create-notes";
import { getNotesWithMoreTemplates } from "api/deepgram";
import ScaleBarrierLoader from "components/scale-barrier-loader";
import { ReactComponent as CheckFilled } from "assets/svgs/check-filled.svg";
import { ReactComponent as CheckUnfilled } from "assets/svgs/check-unfilled.svg";
import { getAllCustomTemplates, getAllTemplates, getAllTemplatesUserIdSortByProfession, getFullTemplateDetails, saveTemplate, updateTemplate } from "api/templates";
import { getUserByCogId } from "api/user";
import ShimmerLoader from "components/shimmer-loader";
import { getAllTopicsByTemplateId } from "api/topic";
import { X, Sparkle, SquarePen, Plus, Pencil, Trash2, PlusIcon, Star, ChevronDown, ChevronUp, Settings2, Save, Search } from 'lucide-react';
import { useFieldArray, useForm } from "react-hook-form";
import { ReactComponent as StarsIcon } from "assets/svgs/stars.svg";
import useMicSettings from "zustand-store/mic";
import useCustomAudioRecorder from "utils/hooks/use-custom-audio-recorder";
import { ReactComponent as CloseIcon } from "assets/svgs/close-modal.svg";
import ReactDOM from "react-dom";
import InputFieldLabel from "components/input-field-label";
import { toast } from "react-toastify";
import Modal from "components/modal";
import DeleteConfirmation from "sections/modal/DeleteConfirmation";
import { createFavoriteTemplate, deleteFavoriteTemplate } from "api/favorite";
import Spinner from "components/Spinner";
import UseExistingTemplateModal from "components/useExistingTemplateModal";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableTopicRow from "components/SortableTopicRow";

const AddMoreTemplates = ({ allTemplates, setActive, setChangesMade, setIsTranscriptEdited, setLoadingTemplates }) => {
  const {
    currentSession,
    transcribedText,
    selectedTemplates,
    setGeneratedNotes,
    setSelectedTemplates,
    currentPatient,
    setTranscribedText,
    setShowSaveSession,
    setSessionDuration,
    setCurrentAudioBlob,
  } = useCreateNotes();

  const { inputDevice } = useMicSettings();

  const { data: userData, isLoading } = useQuery("userProfile", getUserByCogId, {});
  const { data: currentUser } = useQuery("currentUser", getUserByCogId);
  const userId = currentUser?.cognitoId;

  const inputValues = ["Paragraph", "Points"];

  const {
    isPaused,
    audioBlob,
    canvasRef,
    isStopped,
    isRecording,
    audioDuration,
    stopRecording,
    startRecording,
  } = useCustomAudioRecorder(inputDevice?.deviceId);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [moreTemplates, setMoreTemplates] = useState(selectedTemplates);
  const [showChooseTemplateModal, setShowChooseTemplateModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(0);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewData, setPreviewData] = useState({});
  const [previewTopics, setPreviewTopics] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showCustomTemplate, setShowCustomTemplate] = useState(false);
  const [showUseTemplateModal, setShowUseTemplateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [noOfTopics, setNoOfTopics] = useState(1);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [notShowTemplateDescription, setNotShowTemplateDescription] = useState([]);
  const [loadingFavTemplate, setLoadingFavTemplate] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [expandAllTopics, setExpandAllTopics] = useState(false);

  // Reset the expand/collapse state whenever the create/edit modal closes.
  useEffect(() => {
    if (!showCustomTemplate) setExpandAllTopics(false);
  }, [showCustomTemplate]);

  const handleRemoveTopic = (ind) => {
    if (fields.length === 1) {
      toast.error("At least one topic required");
      return;
    }

    remove(ind);
    setNoOfTopics(prev => prev - 1);
  };

  const handlePreviewButtonClick = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const { templateId, templateName, rules, description, topics } = getValues();

    const formattedTopics = topics.map((topic, ind) => ({
      name: topic.name,
      description: topic.prompt,
      id: ind,
    }))

    const template = {
      id: templateId,
      name: templateName,
      description,
      rules,
      topics: formattedTopics,
    };

    handlePreviewClick(template);
  };

  const handleAddTopics = () => {
    if (fields.length >= 10) {
      toast.error("You cannot add more than 10 topics");
      return;
    }

    append({
      name: "",
      summarizationType: "Points",
      prompt: ""
    });
  };

  const closeModal = () => {
    setShowCustomTemplate(false);
    setIsUpdating(false);
    reset({
      templateName: "",
      description: "",
      topics: [
        { summarizationType: "Points", }
      ]
    })
    setNoOfTopics(1);
    setShowDetails(false);
  }

  const { mutate: updateCustomTemplate, isLoading: updateTemplateLoading } = useMutation(
    (data) => updateTemplate(data), {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Updated");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Template update failed.")
    }
  })

  const handleUpdateTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) return;

    const topicNames = data.topics.map(t => t.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (nameSet.size !== topicNames.length) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    updateCustomTemplate(trimmedData);
  };

  const { mutate: saveCustomTemplate, isLoading: saveTemplateLoading } = useMutation(saveTemplate, {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Created");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Failed to create template");
    }
  })

  const deepTrim = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(item => deepTrim(item));
    }

    if (typeof value === "object" && value !== null) {
      const trimmedObj = {};
      Object.keys(value).forEach(key => {
        trimmedObj[key] = deepTrim(value[key]);
      });
      return trimmedObj;
    }

    return value;
  };

  const handleSaveTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) {
      toast.error("Please fill all required fields before updating");
      return;
    }

    const topicNames = data.topics.map(t => t?.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (topicNames.length !== nameSet.size) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    saveCustomTemplate(trimmedData);
  };

  const handlePreviewClick = async (template) => {
    setPreviewTemplate(template);

    if (template.topics && template.topics.length > 0) {
      const filtered = sortTopicsByPosition(template.topics);
      setPreviewTopics(filtered);
      setPreviewData(prev => ({
        ...prev,
        [template.id]: filtered
      }));
      return;
    }

    if (previewData[template.id]) {
      setPreviewTopics(previewData[template.id]);
      return;
    }

    setIsPreviewLoading(true);
    try {
      const topics = await getAllTopicsByTemplateId(template.id);
      const filtered = sortTopicsByPosition(topics);
      setPreviewData(prev => ({
        ...prev,
        [template.id]: filtered
      }));
      setPreviewTopics(filtered);
    } catch (error) {
      console.error('Failed to fetch template topics:', error);
      setPreviewTopics([]);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!updateTemplateLoading)
      setShowDetails(false);
  }, [updateTemplateLoading])

  useEffect(() => {
    if (!saveTemplateLoading)
      setShowDetails(false);
  }, [saveTemplateLoading])

  const handleSubmitNewTemplates = async () => {
    try {
      setIsTranscriptEdited(false);
      setShowChooseTemplateModal(false);
      setLoading(true);
      setLoadingTemplates(true);

      if (moreTemplates.length === 0) {
        handleUnselectedTemplates([]);
        return;
      }

      setSelectedTemplates(moreTemplates);

      const response = await getNotesWithMoreTemplates({
        selectedTemplates: moreTemplates,
        transcribedText,
        sessionId: currentSession.id,
      });

      handleUnselectedTemplates(response.outputs);

    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
      setLoadingTemplates(false);
    }
  };

  const sections = [
    { id: 1, name: 'Nirva Templates' },
    { id: 2, name: 'My Favorites' },
    { id: 3, name: 'Custom Templates' },
  ]

  const sortTopicsByPosition = (topics) => {
    if (!topics) return [];
    return [...topics]
      .filter(t => !t.isDeleted)
      .sort((a, b) => (a.position) - (b.position));
  };

  const customTemplateUpdation = async (e, templateId) => {
    e.stopPropagation();

    const templateData = await getFullTemplateDetails(templateId);

    setNoOfTopics(templateData.topics.length)
    reset({
      templateId: templateData.id,
      templateName: templateData.name,
      description: templateData.description,
      rules: templateData.rules,
      topics: sortTopicsByPosition(templateData.topics).map(topic => ({
        id: topic.id,
        name: topic.name,
        summarizationType: topic.type === "bullet" ? "Points" : topic.type,
        prompt: topic.description,
      }))
    })
    setShowCustomTemplate(true);
    setShowUseTemplateModal(false);
  }

  const closePreview = () => {
    setNotShowTemplateDescription([]);
    setPreviewTemplate(null);
    setPreviewTopics([]);
  };

  const {
    refetch,
    data: outputTemplates = [],
    error: templatesFetchError,
    isLoading: loadingNirvaTemplates
  } = useQuery(
    ["templatesPersonalized", userId],
    () => {
      if (userId) {
        return getAllTemplatesUserIdSortByProfession(userId);
      } else {
        return getAllTemplates();
      }
    },
    {
      enabled: false,
    }
  );

  const { data: customTemplatesData = [], isLoading: customTemplatesLoading } = useQuery('customTemplates',
    getAllCustomTemplates,
  );

  const filteredTemplates = allTemplates?.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredCustomTemplates = customTemplatesData?.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const nirvaAndCustomTemplates = [...allTemplates, ...customTemplatesData];

  const filteredAllTemplates = nirvaAndCustomTemplates.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const { recommendedTemplates, otherTemplates } = useMemo(() => {
    if (!filteredTemplates) return { recommendedTemplates: [], otherTemplates: [] };

    const recommended = [];
    const other = [];

    filteredTemplates.forEach(template => {
      if (template.medicalProfession === "General medicine" || template.recommend === 1) {
        recommended.push(template);
      } else {
        other.push(template);
      }
    });

    return { recommendedTemplates: recommended, otherTemplates: other };
  }, [filteredTemplates]);

  const cannotGenerateNotes =
    loading || templatesFetchError || (moreTemplates || []).length === 0;

  const { control, register, reset, trigger, handleSubmit, getValues, setValue, formState: { errors } }
    = useForm({
      defaultValues: {
        templateName: "",
        description: "",
        rules: "",
        topics: [{
          summarizationType: 'Points'
        }]
      }
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "topics"
  });

  const initialSelectedTemplatesRef = useRef(selectedTemplates);

  const { data: userUsageData } = useQuery("userUsage", getUsageData, {
    enabled: false,
  });

  const queryClient = useQueryClient();

  const getNewlySelectedTemplates = () => {
    return moreTemplates.filter(
      (templateId) => !initialSelectedTemplatesRef.current.includes(templateId)
    );
  };

  const { mutateAsync: createFavTemplate, isLoading: createFavTemplateLoading } = useMutation(
    (data) => createFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Added to favorite templates');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const { mutateAsync: deleteFavTemplate, isLoading: deleteFavTemplateLoading } = useMutation(
    (data) => deleteFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Removed from favorite templates');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const toggleFavoriteTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;

    try {
      if (!isFavorite)
        createFavTemplate({ templateId: template.id })
      else
        deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['templates', userId], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(error.message);
      console.log(error);
    }
  }

  const toggleFavoriteCustomTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;

    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id })
      else
        await deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['customTemplates'], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(error.message);
      console.log(error);
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    const newArr = arrayMove(getValues("topics"), oldIndex, newIndex);
    setValue("topics", newArr);
  };

  const TemplateItem = ({ template, section }) => {
    const { id, name } = template;
    const isSelected = moreTemplates.includes(id);
    const cursorStyles = "hover:bg-blue-50 cursor-pointer";

    return (
      <li key={`${section}-${id}`} className="border-b border-gray-100 last:border-b-0 transition-all duration-200">
        <div
          onClick={() => handleSelectMoreTemplates(template)}
          className={`px-4 flex items-center gap-3 py-3 transition-all duration-200 ${cursorStyles}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {isSelected ? <CheckFilled /> : <CheckUnfilled />}
            </div>
            <span className="flex-1 text-sm font-medium text-black truncate">{name || ""}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewClick(template);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-primary hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <Sparkle size={14} />
              Preview
            </button>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:scale-110"
              disabled={(createFavTemplateLoading || deleteFavTemplateLoading) && (template.id === loadingFavTemplate)}
              onClick={(e) => {
                template.userId === null ? toggleFavoriteTemplate(e, template) : toggleFavoriteCustomTemplate(e, template);
                setLoadingFavTemplate(template.id);
              }}
            >
              {(createFavTemplateLoading || deleteFavTemplateLoading) && template.id === loadingFavTemplate ? (
                <Spinner size={18} />
              ) : (
                <Star 
                  size={20} 
                  fill={template.isFavorite ? '#fbbf24' : 'none'} 
                  stroke="currentColor"
                  strokeWidth={2}
                />
              )}
            </button>
          </div>
        </div>
      </li>
    );
  };

  useEffect(() => {
    setMoreTemplates(selectedTemplates);
  }, [selectedTemplates])

  const hasChanges = () => {
    const initial = [...initialSelectedTemplatesRef.current].sort();
    const current = [...moreTemplates].sort();
    return JSON.stringify(initial) !== JSON.stringify(current);
  };

  const newlySelectedTemplates = getNewlySelectedTemplates();
  const isDisabled = !hasChanges();

  const handleSelectMoreTemplates = (template) => {
    setMoreTemplates((prev) => {
      const prevTemplates = [...prev];
      if (prevTemplates.includes(template.id)) {
        return prevTemplates.filter((ele) => ele !== template.id);
      }
      return [...prevTemplates, template.id];
    });
  };

  useEffect(() => {
    if (showChooseTemplateModal) {
      if (customTemplatesData.length > 0) {
        setSelectedSection(3);
      }
      else if (favoriteTemplates.length > 0) {
        setSelectedSection(2);
      }
      else {
        setSelectedSection(1);
      }
    }
  }, [showChooseTemplateModal]);

  const handleUnselectedTemplates = (newNotes) => {
    const { generatedNotes } = useCreateNotes.getState();

    const existingNotesToKeep = generatedNotes.filter(note =>
      moreTemplates.includes(note.templateId)
    );

    const allNotes = [...existingNotesToKeep, ...newNotes];

    setSelectedTemplates([...moreTemplates]);
    setGeneratedNotes([...newNotes]);

    if (allNotes.length > 0) {
      if (newlySelectedTemplates.length > 0) {
        setActive(newlySelectedTemplates[0]);
      } else {
        setActive(allNotes[0].templateId);
      }
    }

    setChangesMade(true);
    initialSelectedTemplatesRef.current = [...moreTemplates];
  };

  const getButtonTextClass = () => {
    if (loading) return "opacity-0";
    if (cannotGenerateNotes) return "opacity-20";
    return "opacity-100";
  };

  const allFavTemplates = [...allTemplates, ...customTemplatesData];

  const favoriteTemplates = allFavTemplates?.filter(template =>
    template.isFavorite === true &&
    template.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowChooseTemplateModal(prev => !prev)}
          className="flex items-center justify-center text-base font-semibold h-11 hover:scale-110 transition-transform duration-200"
        >
          <AddIcon />
        </button>
      </div>

      {loading && <ScaleBarrierLoader text={"Hang on! Generating notes..."} />}

      {/* Choose template Modal */}
      {showChooseTemplateModal && (
        <>
          {ReactDOM.createPortal(
            <div className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] animate-fade-in ${showUseTemplateModal ? "opacity-20 pointer-events-none" : ""}`} />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none ${showUseTemplateModal ? "opacity-20" : ""}`}>
            <div
              className={`pointer-events-auto w-[90vw] sm:w-[70vw] max-w-[650px] rounded-xl flex flex-col overflow-hidden shadow-2xl animate-scale-in ${showUseTemplateModal ? "pointer-events-none" : ""}`}
            >
              <div className="relative w-full flex flex-col max-h-[85vh]">
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="bg-gray-100 rounded-t-xl border-2 border-gray-200 border-b-0 px-6 py-5 flex flex-col">
                    <div className="flex justify-between items-center w-full mb-4">
                      <h2 className="text-xl font-semibold text-black">
                        Choose Templates
                      </h2>
                      <button
                        onClick={() => {
                          setShowChooseTemplateModal(false);
                          setMoreTemplates(selectedTemplates)
                        }}
                        className="p-2 hover:bg-white/50 rounded-lg transition-all duration-200 transform hover:rotate-90"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-4">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => setSelectedSection(section.id)}
                          className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105 ${
                            selectedSection === section.id
                              ? 'bg-primary text-black shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {section.name}
                        </button>
                      ))}
                    </div>

                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search templates..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 overflow-y-auto bg-white border-2 border-gray-200 border-t-0">
                    {selectedSection === 1 && (
                      <div className="flex flex-col">
                        {loadingNirvaTemplates ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : (
                          <>
                            {recommendedTemplates.length > 0 && (
                              <div>
                                <ul>
                                  {recommendedTemplates.map((template, index) => (
                                    <div
                                      key={`recommended-${template.id}`}
                                      className="animate-fade-in"
                                      style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                      <TemplateItem template={template} section="recommended" />
                                    </div>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {otherTemplates.length > 0 && (
                              <div>
                                <h3 className="px-4 py-3 bg-gray-100 text-gray-900 font-semibold text-sm border-b border-gray-200">
                                  Other Templates
                                </h3>
                                <ul>
                                  {otherTemplates.map((template, index) => (
                                    <div
                                      key={`other-${template.id}`}
                                      className="animate-fade-in"
                                      style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                      <TemplateItem template={template} section="other" />
                                    </div>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {!recommendedTemplates.length && !otherTemplates.length && (
                              <div className="px-4 py-12 text-center text-gray-500">
                                No templates found
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {selectedSection === 2 && (
                      <div className="flex flex-col">
                        {loadingNirvaTemplates ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : (
                          <>
                            {favoriteTemplates.length > 0 ? (
                              <ul>
                                {favoriteTemplates.map((template, index) => (
                                  <div
                                    key={`favorite-${template.id}`}
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                  >
                                    <TemplateItem template={template} section="favorite" />
                                  </div>
                                ))}
                              </ul>
                            ) : (
                              <div className="px-4 py-12 text-center text-gray-500">
                                No favorite templates found
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {selectedSection === 3 && (
                      <div className="flex flex-col">
                        {customTemplatesLoading ? (
                          <div className="p-8">
                            <ShimmerLoader className="w-full h-64" />
                          </div>
                        ) : customTemplatesData.length === 0 ? (
                          <div className="px-4 py-12 text-center">
                            <p className="text-gray-500 mb-6">No custom templates created yet!</p>
                            <div className="flex flex-col gap-3 items-center">
                              <button
                                onClick={() => setShowUseTemplateModal(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                              >
                                <SquarePen size={18} />
                                Use Existing Template
                              </button>
                              <button
                                onClick={() => {
                                  setShowCustomTemplate(true);
                                  setIsUpdating(false);
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                              >
                                <Plus size={18} strokeWidth={3} />
                                Create New Template
                              </button>
                            </div>
                          </div>
                        ) : filteredCustomTemplates.length === 0 ? (
                          <div className="px-4 py-12 text-center text-gray-500">
                            No templates found
                          </div>
                        ) : (
                          <ul>
                            {filteredCustomTemplates.map((customTemplate, index) => (
                              <li
                                key={customTemplate.id}
                                className="border-b border-gray-100 last:border-b-0 animate-fade-in"
                                style={{ animationDelay: `${index * 50}ms` }}
                              >
                                <div
                                  onClick={() => handleSelectMoreTemplates(customTemplate)}
                                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-all duration-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                      {moreTemplates.includes(customTemplate.id) ? (
                                        <CheckFilled />
                                      ) : (
                                        <CheckUnfilled />
                                      )}
                                    </div>
                                    <p className="flex-1 text-sm font-medium text-black">{customTemplate.name}</p>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreviewClick(customTemplate);
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-primary hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                      >
                                        <Sparkle size={14} />
                                        Preview
                                      </button>
                                      <button
                                        disabled={(createFavTemplateLoading || deleteFavTemplateLoading) && (customTemplate.id === loadingFavTemplate)}
                                        onClick={(e) => {
                                          toggleFavoriteCustomTemplate(e, customTemplate);
                                          setLoadingFavTemplate(customTemplate.id);
                                        }}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:scale-110"
                                      >
                                        {(createFavTemplateLoading || deleteFavTemplateLoading) && customTemplate.id === loadingFavTemplate ? (
                                          <Spinner size={18} />
                                        ) : (
                                          <Star
                                            size={20}
                                            fill={customTemplate.isFavorite ? '#fbbf24' : 'none'}
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          />
                                        )}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          setIsUpdating(true);
                                          customTemplateUpdation(e, customTemplate.id);
                                        }}
                                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                      >
                                        <Pencil size={18} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowDeleteModal({
                                            templateId: customTemplate.id,
                                            templateName: customTemplate.name,
                                          });
                                        }}
                                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Floating Action Buttons */}
                        {customTemplatesData.length > 0 && (
                          <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent pt-6 pb-4 px-4 border-t border-gray-200">
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                              <button
                                onClick={() => setShowUseTemplateModal(true)}
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                              >
                                <SquarePen size={18} />
                                Use Existing
                              </button>
                              <button
                                onClick={() => {
                                  setShowCustomTemplate(true);
                                  setIsUpdating(false);
                                }}
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                              >
                                <Plus size={18} strokeWidth={3} />
                                Create New
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="bg-white border-2 border-gray-200 border-t-0 rounded-b-xl px-6 py-4">
                    <button
                      type="button"
                      onClick={handleSubmitNewTemplates}
                      disabled={cannotGenerateNotes}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-black hover:bg-blue-700 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
                    >
                      <span className={getButtonTextClass()}>
                        <StarsIcon />
                      </span>
                      <span className={getButtonTextClass()}>Generate Notes</span>
                    </button>

                    <p className="text-xs text-gray-600 text-center mt-2">
                      <Star className="inline h-3 w-3 mb-0.5 mr-1" fill="#fbbf24" stroke="none" />
                      Favorite templates will be auto-generated during consultation sessions
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showUseTemplateModal &&
        <UseExistingTemplateModal
          setShowUseTemplateModal={setShowUseTemplateModal}
          filteredAllTemplates={filteredAllTemplates}
          handlePreviewClick={handlePreviewClick}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          customTemplateUpdation={customTemplateUpdation}
        />}

      {/* Preview Modal */}
      {previewTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[88] animate-fade-in" onClick={closePreview} />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="pointer-events-auto w-[90vw] max-w-3xl max-h-[85vh] bg-white rounded-xl overflow-hidden shadow-2xl animate-scale-in"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkle className="h-5 w-5 text-blue-600" />
                    {previewTemplate.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Template Preview</p>
                </div>
                <button
                  onClick={closePreview}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:rotate-90"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="mb-6">
                  <p className="text-black text-sm leading-relaxed">
                    {previewTemplate.description}
                  </p>

                  {/*{previewTemplate.rules && previewTemplate.rules.length !== 0 && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-black text-sm mb-2 flex items-center gap-2">
                        <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                        Template Rules
                      </h4>
                      <p className="text-black text-sm leading-relaxed">
                        {previewTemplate.rules}
                      </p>
                    </div>
                  )}*/}
                </div>

                {/* Topics */}
                <div>
                  <h4 className="font-semibold text-black text-base mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-purple-600 rounded-full"></div>
                    Template Topics
                  </h4>
                  <p className="text-black text-sm mb-4">
                    Consultations are categorized and summarized under these topics.
                  </p>
                  {isPreviewLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {previewTopics.map((topic, index) => (
                        <div
                          key={index}
                          className='bg-white border border-gray-400 rounded-lg hover:border-blue-300 transition-all duration-200 overflow-hidden'
                        >
                          <button
                            onClick={() => setNotShowTemplateDescription(prev => {
                              let newIds = [...prev];
                              if (!newIds.includes(topic.id)) {
                                newIds.push(topic.id);
                              } else {
                                newIds = newIds.filter(newTopicId => newTopicId !== topic.id)
                              }
                              return newIds;
                            })}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                          >
                            <h5 className="font-medium text-black text-sm text-left">
                              {topic.name || 'Untitled Topic'}
                            </h5>
                            {notShowTemplateDescription.includes(topic.id) ?
                              <ChevronDown className="h-5 w-5 text-black" /> :
                              <ChevronUp className="h-5 w-5 text-black" />
                            }
                          </button>

                          {!notShowTemplateDescription.includes(topic.id) && (
                            <div className='border-t border-gray-200 p-4 bg-gray-50 text-sm text-black leading-relaxed animate-slide-down'>
                              {topic.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={closePreview}
                  className="px-4 py-2 text-sm font-medium text-black bg-red3 hover:bg-rose-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {/* Create/Edit Template Modal */}
      {showCustomTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[89] animate-fade-in" />,
            document.body
          )}

          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="pointer-events-auto w-[90vw] sm:w-[80vw] lg:w-[70vw] h-[90vh] max-w-[1000px] rounded-xl bg-white shadow-2xl p-6 flex flex-col border border-gray-200 animate-scale-in"
            >
              <div className="relative w-full flex flex-col h-full">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                  <h2 className="font-bold text-2xl text-gray-900">
                    {!isUpdating ? 'Create Custom Template' : 'Update Template'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <CloseIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex flex-col">
                    <InputFieldLabel isRequired={true} label="Template Name" className="mb-1.5 text-sm font-medium" />
                    <input
                      {...register("templateName", { required: "Template name is required" })}
                      placeholder="Enter template name..."
                      className="py-2 px-4 input-field-classes text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors?.templateName && (
                      <span className="text-red-600 text-sm mt-1">{errors.templateName.message}</span>
                    )}
                  </div>

                  {showDetails && (
                    <>
                      <div className="flex flex-col">
                        <InputFieldLabel label="Description" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("description")}
                          className="py-2 px-4 input-field-classes border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Describe your template..."
                        />
                      </div>

                      <div className="flex flex-col">
                        <InputFieldLabel label="Rules" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("rules")}
                          className="py-2 px-4 input-field-classes border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Add any specific rules or guidelines..."
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium self-start"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp size={16} />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show Details
                      </>
                    )}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Topics</h3>
                    <button
                      type="button"
                      onClick={() => setExpandAllTopics(prev => !prev)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      {expandAllTopics ? (
                        <>
                          <ChevronUp size={16} />
                          Collapse all
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Expand all
                        </>
                      )}
                    </button>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {fields.map((field, ind) => (
                        <SortableTopicRow
                          key={field.id}
                          field={field}
                          ind={ind}
                          register={register}
                          errors={errors}
                          getValues={getValues}
                          setValue={setValue}
                          fields={fields}
                          inputValues={inputValues}
                          handleRemoveTopic={handleRemoveTopic}
                          openDropdownIndex={openDropdownIndex}
                          setOpenDropdownIndex={setOpenDropdownIndex}
                          expanded={expandAllTopics}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {fields.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddTopics}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                    >
                      <PlusIcon className="h-5 w-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Add Topic</span>
                    </button>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={handlePreviewButtonClick}
                    disabled={saveTemplateLoading || updateTemplateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-primary hover:bg-primary rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
                  >
                    <Sparkle size={16} />
                    Preview
                  </button>

                  {!isUpdating ? (
                    <button
                      onClick={handleSubmit(handleSaveTemplate)}
                      disabled={saveTemplateLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-green hover:bg-lime-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveTemplateLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  ) : (
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-green hover:bg-lime-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSubmit(handleUpdateTemplate)}
                      disabled={updateTemplateLoading}
                    >
                      {updateTemplateLoading ? 'Updating...' : 'Update Template'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
        >
          <DeleteConfirmation
            data={showDeleteModal}
            setShowDeleteModal={() => setShowDeleteModal(false)}
          />
        </Modal>
      )}

    </>
  );
};

export default AddMoreTemplates;